import { useSaveUserSetting, useUserSetting } from "@/api/services/main/user-setting";
import { IconSave } from "@/assets/icons";
import { ButtonNew } from "@/modules/irohana-components/elements/ButtonNew";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { Form } from "@/modules/irohana-components/elements/Form";
import { LoadingArea } from "@/modules/irohana-components/elements/Loading/Loading";
import { TopPage } from "@/modules/irohana-components/elements/TopPageNew";
import { NotificationEditField } from "@/features/user-settings/components/NotificationEditField";
import { ProfileSection } from "@/features/user-settings/components/ProfileSection";
import { SealSection } from "@/features/user-settings/components/SealSection";
import { SettingCard } from "@/features/user-settings/components/SettingCard";
import { UserSettingContainer } from "@/features/user-settings/components/UserSettingContainer";
import { UserSettingLayout } from "@/features/user-settings/components/UserSettingLayout";
import { USER_SETTING_SECTION_ID, USER_SETTING_SECTION_TITLE } from "@/features/user-settings/constants/sections";
import { TUserSettingForm, toUpdateUserSettingPayload, toUserSettingFormValues, userSettingSchema } from "@/features/user-settings/types/user-setting-form";
import { AppRoutes } from "@/routes/config";
import { setErrorForm, showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Stack } from "@mui/material";
import { FC, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

const UserSettingEdit: FC = () => {
  const navigate = useNavigate();
  const { data, isLoading } = useUserSetting();
  const saveUserSetting = useSaveUserSetting();
  const detail = data?.data;

  const initialValues = useMemo(() => toUserSettingFormValues(detail), [detail]);

  const methods = useForm<TUserSettingForm>({
    resolver: yupResolver(userSettingSchema),
    defaultValues: toUserSettingFormValues(),
  });

  useEffect(() => {
    if (detail) methods.reset(initialValues);
  }, [detail, initialValues, methods]);

  const handleSubmit = async (values: TUserSettingForm) => {
    try {
      await saveUserSetting.mutateAsync({ payload: toUpdateUserSettingPayload(values), hasExistingSetting: Boolean(detail?.id) });
      toast.success("保存しました。");
      navigate(AppRoutes.userSettingDetail);
    } catch (error) {
      setErrorForm(methods, error);
      showError(error);
    }
  };

  if (isLoading) {
    return <LoadingArea height={400} />;
  }

  return (
    <Form methods={methods} onSubmit={handleSubmit} schema={userSettingSchema}>
      <UserSettingContainer>
        <TopPage title="ユーザー設定" enablePageBack={false} />

        <UserSettingLayout
          sections={[
            {
              id: USER_SETTING_SECTION_ID.profile,
              title: USER_SETTING_SECTION_TITLE.profile,
              content: (
                <SettingCard title={USER_SETTING_SECTION_TITLE.profile}>
                  <ProfileSection detail={detail} />
                </SettingCard>
              ),
            },
            {
              id: USER_SETTING_SECTION_ID.notification,
              title: USER_SETTING_SECTION_TITLE.notification,
              content: (
                <SettingCard title={USER_SETTING_SECTION_TITLE.notification}>
                  <NotificationEditField />
                </SettingCard>
              ),
            },
            {
              id: USER_SETTING_SECTION_ID.seal,
              title: USER_SETTING_SECTION_TITLE.seal,
              content: (
                <SettingCard title={USER_SETTING_SECTION_TITLE.seal}>
                  <SealSection detail={detail} />
                </SettingCard>
              ),
            },
          ]}
          footer={
            <FooterContainer>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <ButtonNew variant="tertiary" onClick={() => navigate(AppRoutes.userSettingDetail)}>
                  戻る
                </ButtonNew>
                <ButtonNew type="submit" variant="primary" startIcon={<IconSave />}>
                  保存
                </ButtonNew>
              </Stack>
            </FooterContainer>
          }
        />
      </UserSettingContainer>
    </Form>
  );
};

export default UserSettingEdit;
